package ai.jessie.web.onboarding

import ai.jessie.shared.onboarding.CompleteOnboardingRequest
import ai.jessie.shared.onboarding.EntryResponse
import ai.jessie.shared.onboarding.OnboardingContextResponse
import ai.jessie.shared.onboarding.OrganisationChoiceKind
import ai.jessie.shared.onboarding.OrganisationChoiceResponse
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.ensureActive
import kotlinx.coroutines.launch

data class OnboardingUiState(
    val context: OnboardingContextResponse? = null,
    val firstName: String = "",
    val lastName: String = "",
    val organisationName: String = "",
    val choice: OrganisationChoiceResponse? = null,
    val newOrganisation: Boolean = false,
    val busy: Boolean = false,
    val cancelled: Boolean = false,
    val error: OnboardingProblem? = null,
    val entry: EntryResponse? = null,
)

data class OnboardingActions(
    val firstName: (String) -> Unit,
    val lastName: (String) -> Unit,
    val organisationName: (String) -> Unit,
    val choose: (OrganisationChoiceResponse?) -> Unit,
    val refresh: () -> Unit,
    val submit: () -> Unit,
    val cancel: () -> Unit,
    val resume: () -> Unit,
)

class OnboardingState(private val port: OnboardingPort, private val scope: CoroutineScope) {
    var state by mutableStateOf(OnboardingUiState())
        private set

    val actions =
        OnboardingActions(
            firstName = { if (!state.busy) state = state.copy(firstName = it) },
            lastName = { if (!state.busy) state = state.copy(lastName = it) },
            organisationName = { if (!state.busy) state = state.copy(organisationName = it) },
            choose = {
                if (!state.busy) state = state.copy(choice = it, newOrganisation = it == null)
            },
            refresh = ::refresh,
            submit = ::submit,
            cancel = {
                if (!state.busy && state.context?.complete == false)
                    state = state.copy(cancelled = true, context = null, error = null)
            },
            resume = {
                state = state.copy(cancelled = false)
                refresh()
            },
        )

    fun refresh() = runRequest { loadContext() }

    private suspend fun loadContext() {
        val context = port.context()
        currentCoroutineContext().ensureActive()
        state =
            state.copy(context = context, choice = state.choice?.takeIf { it in context.choices })
        if (context.complete) {
            val entry = port.entry().checked()
            currentCoroutineContext().ensureActive()
            state = state.copy(entry = entry)
        }
    }

    private fun submit() {
        if (state.busy || state.cancelled || state.context == null) return
        val choice = state.choice
        if (!state.newOrganisation && choice == null) {
            state =
                state.copy(
                    error =
                        OnboardingProblem("Bitte wähle eine Organisation oder gründe eine neue.")
                )
            return
        }
        val request =
            CompleteOnboardingRequest(
                firstName = state.firstName,
                lastName = state.lastName,
                choice =
                    if (state.newOrganisation) OrganisationChoiceKind.NEW_ORGANISATION
                    else choice!!.kind,
                organisationId = choice?.organisationId,
                invitationId = choice?.invitationId,
                expectedRole = choice?.role,
                expectedVersion = choice?.version,
                organisationName = if (state.newOrganisation) state.organisationName else null,
            )
        runRequest {
            val entry = port.complete(request).checked()
            currentCoroutineContext().ensureActive()
            state = state.copy(entry = entry)
        }
    }

    // Single-flight, including stale-choice refresh; disposed page scopes cannot publish results.
    private fun runRequest(block: suspend () -> Unit) {
        if (state.busy) return
        state = state.copy(busy = true, error = null)
        scope.launch {
            try {
                block()
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: OnboardingRequestFailure) {
                currentCoroutineContext().ensureActive()
                state = state.copy(error = failure.problem())
                if (failure.refreshChoices()) {
                    state = state.copy(context = null, choice = null)
                    refreshRejectedChoice()
                } else if (state.error?.destination != null || failure.status == HTTP_FORBIDDEN) {
                    state = state.copy(context = null)
                }
            } catch (_: Exception) {
                currentCoroutineContext().ensureActive()
                state = state.copy(error = unavailableProblem())
            } finally {
                currentCoroutineContext().ensureActive()
                state = state.copy(busy = false)
            }
        }
    }

    private suspend fun refreshRejectedChoice() {
        try {
            loadContext()
        } catch (cancelled: CancellationException) {
            throw cancelled
        } catch (failure: OnboardingRequestFailure) {
            currentCoroutineContext().ensureActive()
            state = state.copy(error = failure.problem(), context = null)
        } catch (_: Exception) {
            currentCoroutineContext().ensureActive()
            state = state.copy(error = unavailableProblem(), context = null)
        }
    }
}

internal fun OnboardingRequestFailure.refreshChoices() =
    status == HTTP_CONFLICT || (status == HTTP_BAD_REQUEST && code == "INVALID_CHOICE")
